import { Link } from "react-router-dom";
import { Bot, Radio, Server, Coins, Cpu, AlertTriangle, ChevronRight } from "lucide-react";
import { useAgents } from "@/api/queries/agents";
import { useChannels } from "@/api/queries/channels";
import { useNodes } from "@/api/queries/nodes";
import { useSystemHealth } from "@/api/queries/systemHealth";
import { useAgentsCostSummary } from "@/api/hooks/useCostSummary";
import type { HealthStatus, ComponentHealth, CountHealth } from "@/types";
import { StatCard } from "@/components/StatCard";
import { StatusDot } from "@/components/StatusDot";
import { SectionCard } from "@/components/SectionCard";
import { ActivityFeed } from "@/components/ActivityFeed";
import { phaseLabel } from "@/lib/lifecycleBadge";
import { formatUsd, costThreshold } from "@/lib/formatCost";
import { relativeTime } from "@/lib/relativeTime";
import { cn } from "@/lib/utils";

// #239 C1: Health-Status → Anzeige-Ton.
function healthTone(s: HealthStatus): "ok" | "warn" | "danger" | "muted" {
  return s === "ok" ? "ok" : s === "degraded" ? "warn" : s === "down" ? "danger" : "muted";
}

const PROBLEM_PHASES = ["crashed", "crashloopbackoff", "degraded"];
function problemTone(phase: string): "danger" | "warn" {
  return phase === "degraded" ? "warn" : "danger";
}
// costThreshold → Balken/Text-Ton.
function costTone(v: number): "danger" | "warn" | "muted" {
  const t = costThreshold(v);
  return t === "alert" ? "danger" : t === "warn" ? "warn" : "muted";
}

export function Dashboard() {
  const agents = useAgents();
  const channels = useChannels();
  const nodes = useNodes();
  const cost = useAgentsCostSummary(agents.data);
  const sysHealth = useSystemHealth();
  const sys = sysHealth.data;

  const agentList = agents.data ?? [];
  const active = agentList.filter((a) => !a.revokedAt);
  const running = active.filter((a) => a.lifecyclePhase === "running").length;
  const problem = active.filter((a) => PROBLEM_PHASES.includes(a.lifecyclePhase));
  const channelList = channels.data ?? [];
  const chOnline = channelList.filter((c) => c.status === "online").length;
  const nodeList = nodes.data ?? [];
  const ndOnline = nodeList.filter((n) => n.status === "online").length;
  const totalCost = cost.data.reduce((s, c) => s + c.totalCostUsd, 0);

  // ---- Braucht Aufmerksamkeit ----
  type Attn = { tone: "danger" | "warn" | "muted"; title: string; sub: string; detail: string; to: string; icon: typeof Bot };
  const attention: Attn[] = [];
  problem.forEach((a) =>
    attention.push({ tone: problemTone(a.lifecyclePhase), title: a.name, sub: a.id, detail: phaseLabel(a.lifecyclePhase), to: `/agents/${a.id}`, icon: Bot }),
  );
  channelList.filter((c) => c.status === "offline").forEach((c) =>
    attention.push({ tone: "warn", title: c.name, sub: `kein Heartbeat · ${relativeTime(c.lastHeartbeatAt)}`, detail: "Channel offline", to: `/channels/${c.id}`, icon: Radio }),
  );
  nodeList.filter((n) => n.status === "offline").forEach((n) =>
    attention.push({ tone: "muted", title: n.hostname, sub: relativeTime(n.lastSeen), detail: "Node offline", to: `/nodes/${n.id}`, icon: Server }),
  );

  const topCost = [...cost.data].filter((c) => c.totalCostUsd > 0).sort((a, b) => b.totalCostUsd - a.totalCostUsd).slice(0, 5);
  const maxCost = topCost[0]?.totalCostUsd ?? 1;

  return (
    <div className="space-y-[18px]">
      {/* KPI-Row */}
      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-4" data-testid="dashboard-kpis">
        <StatCard
          to={problem.length > 0 ? "/agents?phase=problem" : "/agents"}
          icon={Bot}
          label="Agents"
          value={agentList.length}
          sub={problem.length > 0 ? `${running} laufen · ${problem.length} mit Problem` : `${running} von ${active.length} laufen`}
          tone={problem.length > 0 ? "alert" : active.length > 0 && running === active.length ? "good" : "neutral"}
        />
        <StatCard
          to="/channels"
          icon={Radio}
          label="Channels"
          value={channelList.length}
          sub={`${chOnline} online`}
          tone={channelList.length === 0 ? "neutral" : chOnline === channelList.length ? "good" : "warn"}
        />
        <StatCard
          to="/nodes"
          icon={Server}
          label="Nodes"
          value={nodeList.length}
          sub={`${ndOnline} online`}
          tone={nodeList.length === 0 ? "neutral" : ndOnline === nodeList.length ? "good" : "warn"}
        />
        <StatCard to="/agents" icon={Coins} label="Kosten" value={formatUsd(totalCost)} sub="Alle Sessions" />
      </div>

      <div className="grid grid-cols-1 items-start gap-[18px] lg:grid-cols-[1.35fr_1fr]">
        {/* LINKS */}
        <div className="flex flex-col gap-[18px]">
          <SectionCard
            testId="needs-attention"
            icon={<AlertTriangle className="h-[18px] w-[18px]" />}
            title="Braucht Aufmerksamkeit"
            description={attention.length > 0 ? `${attention.length} Vorgänge erfordern eine Aktion` : "Alles im grünen Bereich"}
            noPad
          >
            {attention.length === 0 ? (
              <div className="px-[18px] py-10 text-center text-sm text-text3">Keine Probleme — alle Agents, Channels und Nodes laufen.</div>
            ) : (
              attention.map((it, i) => {
                const Icon = it.icon;
                return (
                  <Link
                    key={i}
                    to={it.to}
                    className={cn("flex items-center gap-3 px-[18px] py-[13px] transition-colors hover:bg-surface-hover", i < attention.length - 1 && "border-b border-border")}
                  >
                    <span
                      className={cn(
                        "grid h-[34px] w-[34px] shrink-0 place-items-center rounded-lg",
                        it.tone === "danger" ? "bg-danger-bg text-danger" : it.tone === "warn" ? "bg-warn-bg text-warn" : "bg-muted-bg text-muted",
                      )}
                    >
                      <Icon className="h-[17px] w-[17px]" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13.5px] font-semibold text-text">{it.title}</div>
                      <div className="mono text-xs text-text3">{it.sub}</div>
                    </div>
                    <span
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold",
                        it.tone === "danger" ? "border-danger-border bg-danger-bg text-danger" : it.tone === "warn" ? "border-warn-border bg-warn-bg text-warn" : "border-muted-border bg-muted-bg text-muted",
                      )}
                    >
                      {it.detail}
                    </span>
                    <ChevronRight className="h-4 w-4 text-text3" />
                  </Link>
                );
              })
            )}
          </SectionCard>

          <ActivityFeed />
        </div>

        {/* RECHTS */}
        <div className="flex flex-col gap-[18px]">
          <SectionCard
            testId="system-status"
            icon={<Cpu className="h-[18px] w-[18px]" />}
            title="System-Health"
            description="Alle Komponenten"
            action={
              sys ? (
                <span
                  data-testid="system-overall"
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold",
                    sys.overall === "ok" ? "border-ok-border bg-ok-bg text-ok" : sys.overall === "degraded" ? "border-warn-border bg-warn-bg text-warn" : sys.overall === "down" ? "border-danger-border bg-danger-bg text-danger" : "border-muted-border bg-muted-bg text-muted",
                  )}
                >
                  <StatusDot tone={healthTone(sys.overall)} pulse={sys.overall === "ok"} />
                  {sys.overall === "ok" ? "gesund" : sys.overall === "degraded" ? "beeinträchtigt" : sys.overall === "down" ? "Ausfall" : "unbekannt"}
                </span>
              ) : undefined
            }
          >
            {sysHealth.isError ? (
              <div className="text-sm text-danger" data-testid="server-status-error">Cannot reach server: {(sysHealth.error as Error | undefined)?.message}</div>
            ) : !sys ? (
              <div className="text-sm text-text3">Prüfe…</div>
            ) : (
              <div className="-my-1.5" data-testid="server-status">
                {(() => {
                  const c = sys.components;
                  const rows: { l: string; h: ComponentHealth; count?: string }[] = [
                    { l: "Master REST", h: c.master },
                    { l: "NATS", h: c.nats },
                    { l: "Gitea", h: c.gitea },
                    { l: "Nodes", h: c.nodes, count: `${(c.nodes as CountHealth).healthy}/${(c.nodes as CountHealth).total}` },
                    { l: "Agents", h: c.agents, count: `${(c.agents as CountHealth).healthy}/${(c.agents as CountHealth).total}` },
                    { l: "Channels", h: c.channels, count: `${(c.channels as CountHealth).healthy}/${(c.channels as CountHealth).total}` },
                    // #327: letztes Backup. Nur zeigen, wenn der Master das Feld liefert —
                    // ein älterer Master kennt es nicht, und eine Zeile „unbekannt" wäre
                    // dann eine Aussage über etwas, das niemand geprüft hat.
                    ...(c.backup ? [{ l: "Backup", h: c.backup }] : []),
                  ];
                  return rows.map((r, i, arr) => {
                    const tone = healthTone(r.h.status);
                    return (
                      <div key={r.l} className={cn("flex items-center justify-between py-2.5", i < arr.length - 1 && "border-b border-border")} title={r.h.detail}>
                        <span className="text-[13px] text-text2">{r.l}</span>
                        <span className={cn("mono inline-flex items-center gap-1.5 text-[12.5px] font-semibold", tone === "ok" ? "text-ok" : tone === "warn" ? "text-warn" : tone === "danger" ? "text-danger" : "text-text3")}>
                          {r.count && <span className="tnum text-text3">{r.count}</span>}
                          <StatusDot tone={tone} pulse={tone === "ok"} />
                          {r.h.detail ?? r.h.status}
                        </span>
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </SectionCard>

          <SectionCard testId="node-capacity" icon={<Server className="h-[18px] w-[18px]" />} title="Node-Auslastung" description="Laufende Agents / Kapazität">
            <div className="flex flex-col gap-3.5">
              {nodeList.length === 0 && <div className="text-sm text-text3">Keine Nodes.</div>}
              {nodeList.map((n) => {
                const pct = n.capacity.maxAgents > 0 ? Math.round((n.runningAgents / n.capacity.maxAgents) * 100) : 0;
                const tone = n.status === "offline" ? "bg-muted" : pct > 85 ? "bg-danger" : pct > 65 ? "bg-warn" : "bg-ok";
                return (
                  <div key={n.id}>
                    <div className="mb-1.5 flex items-center justify-between">
                      <span className="mono text-[12.5px] font-semibold text-text">{n.hostname}</span>
                      <span className="tnum text-xs text-text3">{n.status === "offline" ? "offline" : `${n.runningAgents}/${n.capacity.maxAgents}`}</span>
                    </div>
                    <div className="h-[7px] overflow-hidden rounded bg-surface3">
                      <div className={cn("h-full rounded transition-all", tone)} style={{ width: `${n.status === "offline" ? 0 : pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionCard>

          <SectionCard
            testId="top-cost-card"
            icon={<Coins className="h-[18px] w-[18px]" />}
            title="Teuerste Agents"
            description="Summe Session-Kosten"
            action={<span className="mono tnum text-[13px] font-bold text-text">{formatUsd(totalCost)}</span>}
            noPad
          >
            {topCost.length === 0 ? (
              <div className="px-[18px] py-8 text-center text-sm text-text3">Noch keine Kosten erfasst.</div>
            ) : (
              <div className="px-[18px] py-1.5">
                {topCost.map((c, i) => (
                  <Link key={c.agentId} to={`/agents/${c.agentId}`} className={cn("flex items-center gap-2.5 py-2.5", i < topCost.length - 1 && "border-b border-border")}>
                    <span className="tnum w-3.5 text-xs font-bold text-text3">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[13px] font-semibold text-text">{c.agentName}</div>
                      <div className="mt-1.5 h-[5px] overflow-hidden rounded bg-surface3">
                        <div className={cn("h-full rounded", costTone(c.totalCostUsd) === "danger" ? "bg-danger" : costTone(c.totalCostUsd) === "warn" ? "bg-warn" : "bg-muted")} style={{ width: `${(c.totalCostUsd / maxCost) * 100}%` }} />
                      </div>
                    </div>
                    <span className={cn("mono tnum text-[12.5px] font-semibold", costTone(c.totalCostUsd) === "danger" ? "text-danger" : costTone(c.totalCostUsd) === "warn" ? "text-warn" : "text-text3")}>
                      {formatUsd(c.totalCostUsd)}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
